import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AuthUtils } from '../auth.utils';
import { environment } from 'src/environments/environment';
import {
  LoginRequestDTO,
  LoginResponseDTO,
  UsuarioInfoDTO,
} from '../models/auth.models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _authenticated = false;
  private _usuario = signal<UsuarioInfoDTO | null>(null);

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get refreshToken(): string {
    return localStorage.getItem('refreshToken') ?? '';
  }

  set refreshToken(token: string) {
    localStorage.setItem('refreshToken', token);
  }

  get usuario() {
    return this._usuario;
  }

  private set usuarioPersistido(u: UsuarioInfoDTO | null) {
    this._usuario.set(u);
    if (u) {
      localStorage.setItem('usuario', JSON.stringify(u));
    } else {
      localStorage.removeItem('usuario');
    }
  }

  constructor(private _httpClient: HttpClient) {
    // Restaurar el usuario de la sesión persistida (sobrevive a recargas de página)
    const raw = localStorage.getItem('usuario');
    if (raw) {
      try {
        this._usuario.set(JSON.parse(raw) as UsuarioInfoDTO);
      } catch {
        localStorage.removeItem('usuario');
      }
    }
  }

  signIn(credentials: LoginRequestDTO): Observable<LoginResponseDTO> {
    if (this._authenticated) {
      return throwError(() => new Error('El usuario ya tiene una sesión activa.'));
    }

    return this._httpClient
      .post<LoginResponseDTO>(`${environment.apiUrl}/auth/auth/login`, credentials)
      .pipe(
        tap((response) => {
          this.accessToken = response.accessToken;
          this.refreshToken = response.refreshToken;
          this._authenticated = true;
          this.usuarioPersistido = response.usuario;
        })
      );
  }

  signInUsingToken(): Observable<boolean> {
    if (!this.refreshToken) {
      return of(false);
    }

    return this._httpClient
      .post<LoginResponseDTO>(`${environment.apiUrl}/auth/auth/refresh`, {
        refreshToken: this.refreshToken,
      })
      .pipe(
        tap((response) => {
          this.accessToken = response.accessToken;
          this.refreshToken = response.refreshToken;
          this._authenticated = true;
          this.usuarioPersistido = response.usuario;
        }),
        switchMap(() => of(true)),
        catchError(() => of(false))
      );
  }

  signOut(): Observable<boolean> {
    const rt = this.refreshToken;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('usuario');
    this._authenticated = false;
    this._usuario.set(null);

    if (rt) {
      return this._httpClient
        .post<any>(`${environment.apiUrl}/auth/auth/logout`, { refreshToken: rt })
        .pipe(
          switchMap(() => of(true)),
          catchError(() => of(true))
        );
    }

    return of(true);
  }

  forgotPassword(email: string): Observable<{ [key: string]: string }> {
    return this._httpClient.post<{ [key: string]: string }>(
      `${environment.apiUrl}/auth/auth/recuperar-password`,
      { email }
    );
  }

  check(): Observable<boolean> {
    if (this._authenticated) {
      return of(true);
    }

    if (!this.accessToken) {
      return of(false);
    }

    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return this.signInUsingToken();
    }

    this._authenticated = true;
    return of(true);
  }
}
